/**
 * Client helpers for the firm-website onboarding gate (ENG-3641).
 *
 * Prefill mirrors `railyard.accounts.CONSUMER_EMAIL_DOMAINS` +
 * `corporate_domain_from_email`: a work-email domain becomes the
 * suggested firm website; consumer mailboxes leave the field blank.
 */

/** Personal mailboxes that never indicate a firm — keep in sync with
 * `CONSUMER_EMAIL_DOMAINS` in `src/railyard/accounts.py`. */
const CONSUMER_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
]);

/**
 * Suggest a firm website host from a signed-in email address.
 * Returns `""` for missing/malformed emails and consumer domains.
 */
export function firmWebsitePrefill(email: string | null | undefined): string {
  if (!email || !email.includes("@")) return "";
  const domain = email.split("@").pop()?.trim().toLowerCase().replace(/\.$/, "") ?? "";
  if (!domain || !domain.includes(".") || CONSUMER_EMAIL_DOMAINS.has(domain)) {
    return "";
  }
  return domain;
}

/**
 * Light client-side check before submit — the server still normalizes and
 * validates. Accepts bare hosts (`acme.com`) or URLs (`https://acme.com/…`).
 */
export function looksLikeFirmWebsite(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  const host = s
    .replace(/^https?:\/\//i, "")
    .split("/", 1)[0]
    .split("?", 1)[0]
    .split("#", 1)[0]
    .split(":")[0]
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
  if (!host || !host.includes(".") || /[@/\s]/.test(host)) return false;
  return true;
}
